import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTable } from 'components/datatable/NewTable'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { isNil } from 'lodash'
import { nbPerPageLabelShorty } from 'referencial/constants/ReferencialConstants'
import useSandreList from 'utils/customHook/useSandreList'
import { exportFile } from 'utils/ExportDataUtil'
import OperationAction from 'quality/components/operation/actions/OperationAction'
import { pocheShape } from 'quality/components/operation/shapes/OperationShapes'

const toInt = v => (v === null || v === undefined || v === '') ? undefined : parseInt(v, 10)
const toStr = v => (v === null || v === undefined || v === '') ? undefined : `${v}`

const formatRow = row => {
    const formatted = { ...row }
    NUMERIC_KEYS.forEach(k => {
        if (typeof row[k] === 'number') formatted[k] = row[k].toFixed(2)
    })
    return formatted
}

const HydrobioCountDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    onImport = () => {},
    title = 'Modification du dénombrement',
    poche = {},
    individuals = [],
    isNew = false,
}) => {
    const {
        taxons,
        remarks,
        units,
        sandreCodes,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        remarks: store.OperationReducer.remarks,
        units: store.UnitReducer.units,
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const [taxon, setTaxon] = useState(poche.taxon)
    const [sampling, setSampling] = useState(poche.sampling)
    const [unit, setUnit] = useState(poche.unit)
    const [remark, setRemark] = useState(poche.remark)
    const [deadCount, setDeadCount] = useState(poche.deadCount)
    const [initialTotalWeight, setInitialTotalWeight] = useState(poche.initialTotalWeight)
    const [totalWeight, setTotalWeight] = useState(poche.totalWeight)
    const [growth, setGrowth] = useState(poche.growth)
    const [development, setDevelopment] = useState(poche.development)
    const [size, setSize] = useState(poche.size)
    const fileInputRef = useRef()

    useEffect(() => {
        setTaxon(poche.taxon)
        setSampling(poche.sampling)
        setUnit(poche.unit)
        setRemark(poche.remark)
        setDeadCount(poche.deadCount)
        setInitialTotalWeight(poche.initialTotalWeight)
        setTotalWeight(poche.totalWeight)
        setGrowth(poche.growth)
        setDevelopment(poche.development)
        setSize(poche.size)
    }, [poche.taxon, poche.sampling])

    const COUNT_HEADERS = [
        { key: 'individual', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.individualNumber}</span> },
        { key: 'length', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.length} (mm)`}</span> },
        { key: 'width', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.width} (mm)`}</span> },
        { key: 'thickness', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.thickness} (mm)`}</span> },
        { key: 'weight', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.weight} (g)`}</span> },
        { key: 'emptyShellWeight', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.emptyShellWeight} (g)`}</span> },
        { key: 'drainedFleshWeight', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.drainedFleshWeight} (g)`}</span> },
        { key: 'dryFleshWeight', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.dryFleshWeight} (g)`}</span> },
        { key: 'polydoraIndex', value: <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.polydoraIndex} (A/P)`}</span> },
        { key: 'shapeIndex', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.shapeIndex}</span> },
        { key: 'imaiSakai', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.imaiSakai}</span> },
        { key: 'fleshRate', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.fleshRate}</span> },
    ]

    const EXPORT_HEADERS = ['individualNumber', 'length', 'width', 'thickness', 'weight', 'emptyShellWeight', 'drainedFleshWeight', 'dryFleshWeight', 'polydoraIndex', 'shapeIndex', 'imaiSakai', 'fleshRate']

    const NUMERIC_KEYS = COUNT_HEADERS.map(h => h.key).filter(k => k !== 'individual' && k !== 'polydoraIndex')

    const developLexicon = useSandreList('TAXONS.DEVELOPPEMENT')
    const sizeLexicon = useMemo(() => {
        const specific = isNil(taxon) ? undefined : sandreCodes.filter(c => c.field === `TAXONS.${taxon}.TAILLE`)
        return specific?.length ? specific : sandreCodes.filter(c => c.field === 'TAXONS.TAILLE')
    }, [sandreCodes, taxon])

    const taxonLabel = useMemo(() => {
        if (isNil(taxon)) return ''
        const foundTaxon = taxons.find(t => `${t.code}` === `${taxon}`)
        return foundTaxon?.labelWithCode || `${taxon}`
    }, [taxons, taxon])

    const [samplings, setSamplings] = useState([])
    useEffect(() => {
        if (isOpen && poche.qualitometer && poche.operation) {
            OperationAction.promiseSampleSamplings(poche.qualitometer, poche.operation).then(setSamplings)
        }
    }, [isOpen, poche.qualitometer, poche.operation])

    const samplingLabel = useMemo(() => {
        if (isNil(poche.sampling)) return ''
        return samplings.find(s => `${s.idSampling}` === `${poche.sampling}`)?.ref || `${poche.sampling}`
    }, [samplings, poche.sampling])

    const rows = useMemo(() => individuals.map(ind => formatRow({
        individual: ind.individualId,
        length: ind.length,
        width: ind.width,
        thickness: ind.thickness,
        weight: ind.weight,
        emptyShellWeight: ind.emptyShellWeight,
        drainedFleshWeight: ind.drainedFleshWeight,
        dryFleshWeight: ind.dryFleshWeight,
        polydoraIndex: ind.polydoraIndex,
        shapeIndex: ind.shapeIndex,
        imaiSakai: ind.imaiSakai,
        fleshRate: ind.fleshRate,
    })), [individuals])

    const onExport = () => {
        const data = individuals.map(ind => ({
            individualNumber: ind.individualId,
            length: ind.length,
            width: ind.width,
            thickness: ind.thickness,
            weight: ind.weight,
            emptyShellWeight: ind.emptyShellWeight,
            drainedFleshWeight: ind.drainedFleshWeight,
            dryFleshWeight: ind.dryFleshWeight,
            polydoraIndex: ind.polydoraIndex,
            shapeIndex: ind.shapeIndex,
            imaiSakai: ind.imaiSakai,
            fleshRate: ind.fleshRate,
        }))
        exportFile({
            data: [{ ...(data[0] || {}), headers: EXPORT_HEADERS }, ...data.slice(1)],
            exportType: 'xlsx',
            titleFile: `poche_${poche.taxon}_${poche.sampling}`,
        })
    }

    const handleImportFile = e => {
        const importedFile = e.target.files?.[0]
        if (!importedFile) return
        const reader = new FileReader()
        reader.onload = () => onImport({ taxon: poche.taxon, sampling: poche.sampling, fileName: importedFile.name, base64data: reader.result.split(',')[1] })
        reader.readAsDataURL(importedFile)
        e.target.value = ''
    }

    return (
        <Dialog maxWidth='xl' fullWidth open={isOpen}>
            <DefaultDialogTitle title={title} onClose={onClose} />
            <DialogContent sx={{ overflowX: 'hidden' }}>
                <Stack spacing={1}>
                    <Card>
                        <CardContent>
                            <Grid container rowSpacing={1} columnSpacing={2}>
                                <Grid size={4}>
                                    {isNew ? (
                                        <Select
                                            label={i18n.taxon}
                                            options={taxons}
                                            value={taxon}
                                            keyLabel='labelWithCode'
                                            keyValue='code'
                                            returnNull
                                            onChange={v => setTaxon(toInt(v))}
                                        />
                                    ) : (
                                        <Input title={i18n.taxon} value={taxonLabel} disabled />
                                    )}
                                </Grid>
                                <Grid size={4}>
                                    {isNew ? (
                                        <Select
                                            label={i18n.sampling}
                                            options={samplings}
                                            value={sampling}
                                            keyLabel='ref'
                                            keyValue='idSampling'
                                            returnNull
                                            onChange={v => setSampling(toInt(v))}
                                        />
                                    ) : (
                                        <Input title={i18n.sampling} value={samplingLabel} disabled />
                                    )}
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.unit}
                                        options={units}
                                        value={unit}
                                        keyLabel='symbolWithName'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => setUnit(toStr(v))}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.remark}
                                        options={remarks}
                                        value={remark}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => setRemark(toInt(v))}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <NumberField title={i18n.deadIndividualsNb} value={deadCount} onChange={setDeadCount} />
                                </Grid>
                                <Grid size={4}>
                                    <NumberField title={i18n.initialTotalWeight} value={initialTotalWeight} onChange={setInitialTotalWeight} floatValue />
                                </Grid>
                                <Grid size={4}>
                                    <NumberField title={i18n.totalWeight} value={totalWeight} onChange={setTotalWeight} floatValue />
                                </Grid>
                                <Grid size={4}>
                                    <NumberField title={i18n.growth} value={growth} onChange={setGrowth} floatValue />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.development}
                                        options={developLexicon}
                                        value={development}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => setDevelopment(toInt(v))}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.size}
                                        options={sizeLexicon}
                                        value={size}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => setSize(toInt(v))}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {!isNew && (
                        <>
                            <input
                                type='file'
                                ref={fileInputRef}
                                accept='.csv, .xls, .xlsx'
                                style={{ display: 'none' }}
                                onChange={handleImportFile}
                            />
                            <CardTable
                                title={i18n.individualsList}
                                rows={rows}
                                headers={COUNT_HEADERS}
                                rowsPerPageOptions={nbPerPageLabelShorty}
                                actions={[
                                    { icon: 'file_upload', tooltip: i18n.importLabel, onClick: () => fileInputRef.current?.click() },
                                    { icon: 'file_download', tooltip: i18n.export, onClick: onExport },
                                ]}
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onValidate({ ...poche, taxon, sampling, unit, remark, deadCount, initialTotalWeight, totalWeight, growth, development, size })}
                    disabled={isNew && (isNil(taxon) || isNil(sampling))}
                    variant='contained'
                    color='primary'
                >
                    {i18n.save}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

HydrobioCountDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    onImport: PropTypes.func,
    isNew: PropTypes.bool,
    title: PropTypes.string,
    poche: pocheShape,
    individuals: PropTypes.arrayOf(PropTypes.object),
}

export { HydrobioCountDialog }